<template>
  <!-- Rendered straight into .lz-modal-body, which already provides the
       dialog padding, so there is no b-container/row wrapper here. -->
  <div class="lz-controls lz-allowance-panel">
    <div class="lz-allowance-head">
      <b-form-radio-group
        class="allowance-year-group"
        v-model="selectedYear"
        :options="years"
        button-variant="outline-secondary"
        name="allowance-years"
        buttons></b-form-radio-group>
      <p class="lz-allowance-hint">
        Allowance, carry-forward and in-lieu are editable. Changes save when you
        leave the field.
      </p>
    </div>
    <b-alert :show="!!saveError" variant="danger" class="lz-allowance-error">
      {{ saveError }}
    </b-alert>
    <div class="lz-summary-grid">
      <div
        v-for="card in summaryCards"
        :key="`${selectedYear}-${card.key}`"
        class="lz-stat-card">
        <div class="lz-stat-card-title">{{ card.title }}</div>
        <div class="lz-stat-card-figure">
          <span class="lz-stat-card-value">{{ card.headlineValue }}</span>
          <span class="lz-stat-card-unit" v-if="card.headlineLabel">{{
            card.headlineLabel
          }}</span>
        </div>
        <ul class="lz-stat-card-rows" v-if="card.rows.length">
          <li
            v-for="row in card.rows"
            :key="row.label"
            :class="{ 'is-editable': row.editable }">
            <span class="allowance-label">{{ row.label }}</span>
            <!-- Editable or not, every value sits in the same .allowance-field
                 box so the digits line up down the card. -->
            <span class="allowance-field">
              <b-form-input
                type="number"
                size="sm"
                class="allowance-input"
                :class="{ 'is-readonly': !row.editable }"
                :data-group="row.group"
                :value="row.value"
                :readonly="!row.editable"
                :disabled="!row.editable || savingGroup === row.group"
                @blur="updateAllowance"></b-form-input>
              <b-icon
                v-if="savedGroup === row.group"
                icon="check2"
                class="allowance-saved"
                aria-hidden="true"></b-icon>
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Services from '../../../services/main.service.vue'

// Unpaid, maternity/paternity and WFH (outside UK) are not capped by an
// allowance, so their figure is always 0 and the row is dropped rather than
// implying there is a budget to spend.
const TypesWithoutAllowance = ['UNPAID', 'PATERNITY', 'WFH']

// The three allowance-bearing types lead, so with the three-column grid below
// they fill the top row and the rest fall to the bottom one. Anything the API
// adds later sorts to the end rather than breaking the split.
const TypeOrder = ['ANNUAL', 'PERSONAL', 'BIRTHDAY', 'UNPAID', 'PATERNITY', 'WFH']
const typeRank = (key) => {
  const index = TypeOrder.indexOf(key)
  return index < 0 ? TypeOrder.length : index
}

export default {
  name: 'HolidayStats',
  props: ['statsData'],
  computed: {
    // One card per leave type, mirroring logs-components/Holiday/Stats.vue.
    // The difference here is that the fields an admin owns (allowance,
    // carry-forward, in-lieu) are always rendered as inputs, even when empty,
    // so there is somewhere to type.
    summaryCards() {
      const items = (this.statItems[this.selectedYear] || [])
        .slice()
        .sort((a, b) => typeRank(a.key) - typeRank(b.key))
      return items.map((itm) => {
        const allowance = itm.groups?.[itm.key] || itm.allowance || ''
        const hasBalance = ![null, undefined, ''].includes(itm.balance)
        const editable = ['ANNUAL', 'PERSONAL'].includes(itm.key)
        const rows = []
        let headlineValue = '—'
        let headlineLabel = ''

        // Admin view keeps the raw balance, negatives included: this is the
        // screen where an over-booked allowance has to be visible.
        if (hasBalance) {
          headlineValue = itm.balance
          headlineLabel = 'days left'
        } else if (allowance !== '') {
          headlineValue = allowance
          headlineLabel = 'days allowance'
        }

        // Allowance is on every card that has one, zero included, so there is
        // always somewhere to type. Only editable inputs stay blank when unset.
        if (!TypesWithoutAllowance.includes(itm.key)) {
          rows.push(
            editable
              ? {
                  label: 'Allowance',
                  value: allowance,
                  group: itm.key,
                  editable: true
                }
              : { label: 'Allowance', value: allowance === '' ? 0 : allowance }
          )
        }

        if (itm.key === 'ANNUAL') {
          rows.push({
            label: 'Carry-forward',
            value: itm.groups?.CARRYFORWARD || '',
            group: 'CARRYFORWARD',
            editable: true
          })
          rows.push({
            label: 'In-lieu',
            value: itm.groups?.INLIEU || '',
            group: 'INLIEU',
            editable: true
          })
        }
        // Nothing pending, taken or turned down is the normal state, so zeros
        // on these three are noise: a card for a leave type nobody has touched
        // is just its allowance.
        if (Number(itm.pending) > 0) {
          rows.push({ label: 'Pending', value: itm.pending })
        }
        if (Number(itm.approved) > 0) {
          rows.push({ label: 'Approved', value: itm.approved })
        }
        if (Number(itm.rejected) > 0) {
          rows.push({ label: 'Rejected', value: itm.rejected })
        }

        return {
          key: itm.key,
          title: String(itm.value || '').toUpperCase(),
          headlineValue,
          headlineLabel,
          rows
        }
      })
    }
  },
  data: () => {
    return {
      statItems: {},
      selectedYear: null,
      years: [],
      // { [year]: { ANNUAL: n, CARRYFORWARD: n, ..., BIRTHDAY: n } } — every
      // allowance key the API knows about, including the ones this screen
      // does not display. See setLeaveAllowance().
      allGroups: {},
      leaveAllowances: {},
      savingGroup: '',
      savedGroup: '',
      saveError: ''
    }
  },
  mounted() {
    const now = new Date()
    this.selectedYear = now.getFullYear()
    this.sortStats()
    this.setLeaveAllowance()
  },
  methods: {
    sortStats() {
      const TypesToHide = ['ANNUALSALE']
      const { years, allowance } = this.statsData
      const statItems = {}
      const allGroups = {}
      this.years = years
      years.forEach((year) => {
        statItems[year] = allowance[year]
          .map((itm) => {
            // Snapshot taken before the display tweaks below blank
            // groups.PERSONAL, and before ANNUALSALE is filtered out, so the
            // save payload can carry every key back untouched.
            allGroups[year] = Object.assign(allGroups[year] || {}, itm.groups)
            itm.balance = ''
            if (itm.value.toLowerCase() == 'personal') {
              itm.value = 'Personal/Sick'
              itm.groups[itm.key] = ''
              // itm.balance = itm.remains
            }
            if (
              ['annual holiday', 'personal/sick', 'birthday'].includes(
                itm.value.toLowerCase()
              )
            ) {
              itm.balance = itm.remains
            }
            itm.pending = itm.pending || ''
            itm.approved = itm.approved || ''
            return itm
          })
          .filter((itm) => {
            return !TypesToHide.includes(itm.key)
          })
      })
      // Assigned in one go: adding keys to the existing object would not be
      // reactive in Vue 2, and the cards are rendered from a computed.
      this.statItems = statItems
      this.allGroups = allGroups
    },
    // The API merges the posted leaveAllowances per *year*, so a year object
    // sent here replaces the stored one wholesale. Sending only the four
    // editable fields therefore wiped every other key on that year — that is
    // how a birthday allowance of 1 ended up as 0 after any edit. Rebuild the
    // full set instead.
    setLeaveAllowance() {
      const leaveAllowances = {}
      for (const year in this.allGroups) {
        leaveAllowances[year] = Object.keys(this.allGroups[year]).reduce(
          (acc, group) => {
            acc[group.toLowerCase()] = Number(this.allGroups[year][group]) || 0
            return acc
          },
          {}
        )
      }
      this.leaveAllowances = leaveAllowances
    },
    // A saved value has to land back in both the payload source and the cards,
    // otherwise editing a second field would re-send the pre-edit value for
    // the first one.
    applyToStatItems(group, value) {
      const items = this.statItems[this.selectedYear] || []
      const annual = items.find((itm) => itm.key === 'ANNUAL')
      if (group === 'PERSONAL') {
        const personal = items.find((itm) => itm.key === 'PERSONAL')
        if (personal) {
          personal.allowance = value
        }
        return
      }
      if (annual) {
        annual.groups[group] = value
      }
    },
    async updateAllowance(ev) {
      const {
        value,
        dataset: { group }
      } = ev.target
      const user = this.statsData.user
      const numeric = Number(value || 0)
      const yearGroups = this.allGroups[this.selectedYear]
      // Readonly rows carry no group and cannot fire blur while disabled, but
      // the handler is bound to every input, so bail out defensively.
      if (!group || !yearGroups) {
        return
      }
      yearGroups[group] = numeric
      this.setLeaveAllowance()
      this.savingGroup = group
      this.saveError = ''
      try {
        await Services.Users.Admin.updateUser({
          uid: user,
          data: {
            leaveAllowances: this.leaveAllowances
          }
        })
        this.applyToStatItems(group, numeric)
        this.savedGroup = group
        setTimeout(() => {
          if (this.savedGroup === group) {
            this.savedGroup = ''
          }
        }, 1800)
      } catch (err) {
        console.log({ err })
        this.saveError = 'Could not save the allowance. Please try again.'
      } finally {
        this.savingGroup = ''
      }
    }
  }
}
</script>

<!-- The cards themselves (.lz-summary-grid / .lz-stat-card*) and
     .allowance-year-group are styled centrally in src/styles/logs.css, so this
     screen matches the user-facing leave summary. Only the editable field is
     specific to the admin view. -->

<style scoped>
/* Year pills and the hint share a line on wide dialogs and stack below the
   xl breakpoint, where the pills take the full width. */
.lz-allowance-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 18px;
  margin-bottom: 18px;
}
.lz-allowance-hint {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
}
.lz-allowance-error {
  font-size: 13px;
}
/* Three fixed columns rather than the shared auto-fit track list, so the six
   leave types always split 3 + 3: the allowance-bearing ones on top, the rest
   below. The grid's own bottom margin would also double up with the modal
   body padding. */
.lz-summary-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 0;
}
@media (max-width: 767.98px) {
  .lz-summary-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

.allowance-label {
  flex: 1 1 auto;
  min-width: 0;
}
/* One fixed-width column for every value on the card, so the digits line up
   whether the row is editable or not. It sits inside the card's padding: the
   earlier negative margin pushed the field over the card edge and took the
   digits with it. */
.allowance-field {
  position: relative;
  flex: 0 0 64px;
  display: inline-flex;
  align-items: center;
}
/* The field's own appearance — fill, border, focus ring, disabled contrast —
   lives in .lz-controls .allowance-input.form-control in src/styles/logs.css.
   It has to: the generic .lz-controls .form-control rule sets padding, border
   and colour with !important, which a scoped rule here cannot out-rank. */
/* Sits outside the field rather than in it, so the tick appearing after a save
   cannot nudge the number out of line. */
.allowance-saved {
  position: absolute;
  right: 100%;
  margin-right: 6px;
  color: #16a34a;
  font-size: 14px;
}
</style>
